import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAttendanceEmployees, AttendanceEmployee } from "../api/attendanceApi";

/**
 * Employee list for a dropdown that's normally fed by a parent's own
 * already-loaded list (employeesProp), but needs to reliably self-fetch
 * when that isn't available (yet, or ever) — shared by AttendanceActionModal
 * and LeaveRequestModal, which both need identical fallback-fetch/retry/
 * race-safety behavior (previously duplicated near-verbatim in both, which
 * is exactly how a fix to one and not the other happened once already).
 *
 * `enabled` gates the self-fetch entirely (e.g. the field isn't editable in
 * this context, so there's nothing to populate). An empty employeesProp
 * array is treated as "not yet provided" (parent's own fetch still in
 * flight, or hasn't started) rather than "confirmed empty", so this still
 * self-fetches instead of getting stuck empty — see AttendancePage's own
 * employees state, which is `undefined` (not `[]`) until its fetch
 * resolves for the same reason.
 */
export function useEmployeeList(enabled: boolean, employeesProp?: AttendanceEmployee[]) {
  const [employees, setEmployees] = useState<AttendanceEmployee[]>(employeesProp ?? []);
  const [loadError, setLoadError] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Whether the prop has supplied real data at any point — read from the
  // self-fetch's .catch (which can resolve well after this ref updates),
  // so a slower/flaky self-fetch failure arriving after the parent's data
  // already came through doesn't replace a working list with a false
  // "couldn't load" screen.
  const hasPropDataRef = useRef(false);
  useEffect(() => {
    hasPropDataRef.current = !!(employeesProp && employeesProp.length > 0);
  }, [employeesProp]);

  const loadEmployees = useCallback(() => {
    setLoadError(false);
    fetchAttendanceEmployees()
      .then((list) => { if (mountedRef.current) setEmployees(list); })
      .catch(() => { if (mountedRef.current && !hasPropDataRef.current) setLoadError(true); });
  }, []);

  useEffect(() => {
    if (!enabled || (employeesProp && employeesProp.length > 0)) return;
    loadEmployees();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (employeesProp && employeesProp.length > 0) {
      setEmployees(employeesProp);
      setLoadError(false);
    }
  }, [employeesProp]);

  return { employees, loadError, retry: loadEmployees };
}
